import type { RatingDto } from '@/types/compare'

const USER_SCORE_OVERRIDES_KEY = 'modelscout_mock_user_score_overrides'

export interface MockRatingTarget {
  modelId: string
  categoryId: string
}

/** Mirrors backend UserScoreCalculator for Playwright mock flows. */
export function computeMockUserScore(ratings: RatingDto[]): number {
  if (ratings.length === 0) return 0

  const thumbsUpCount = ratings.filter((rating) => rating.thumbs === 'up').length
  const starRatings = ratings
    .map((rating) => rating.ratingStars)
    .filter((stars): stars is number => typeof stars === 'number' && stars >= 1 && stars <= 5)
  const bestAnswerCount = ratings.filter((rating) => rating.isBestAnswer).length

  const thumbsScore = (thumbsUpCount / ratings.length) * 0.4
  const starsScore =
    starRatings.length === 0
      ? 0
      : ((starRatings.reduce((sum, stars) => sum + stars, 0) / starRatings.length - 1) / 4) * 0.4
  const bestAnswerBonus = Math.min(0.2, (bestAnswerCount / ratings.length) * 0.2)

  return Math.min(1, thumbsScore + starsScore + bestAnswerBonus)
}

function overrideKey(modelId: string, categoryId: string) {
  return `${modelId}:${categoryId}`
}

export function loadMockUserScoreOverrides(): Record<string, number> {
  if (typeof sessionStorage === 'undefined') return {}

  const raw = sessionStorage.getItem(USER_SCORE_OVERRIDES_KEY)
  if (!raw) return {}

  try {
    return JSON.parse(raw) as Record<string, number>
  } catch {
    return {}
  }
}

export function saveMockUserScoreOverride(modelId: string, categoryId: string, userScore: number) {
  if (typeof sessionStorage === 'undefined') return

  const overrides = loadMockUserScoreOverrides()
  overrides[overrideKey(modelId, categoryId)] = userScore
  sessionStorage.setItem(USER_SCORE_OVERRIDES_KEY, JSON.stringify(overrides))
}

export function getMockUserScoreOverride(
  modelId: string,
  categoryId: string,
): number | undefined {
  return loadMockUserScoreOverrides()[overrideKey(modelId, categoryId)]
}

export function resetMockUserScoreOverrides() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(USER_SCORE_OVERRIDES_KEY)
}
